import {
  Color,
  ImageCoverView,
  ImageScaleView,
  TintFilter,
} from '@play-co/timestep-core/ui';
import bitmapFonts from 'src/lib/bitmapFonts';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import sounds from 'src/lib/sounds';
import ButtonScaleView, {
  ButtonScaleViewOpts,
} from 'src/lib/ui/components/ButtonScaleView';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { LockedReason } from '../popups/PopupFeatureDisabled';

export type SideButtonFeatures =
  | 'duo'
  | 'visitFriend'
  | 'daycare'
  | 'wardrobe'
  | 'fullRecovery';

type SideButtonOpts = ButtonScaleViewOpts & {
  icon: {
    image: string;
    width?: number;
    height?: number;
    offsetX?: number;
    offsetY?: number;
  };
  label: string;
};

export class SideButton extends ButtonScaleView {
  private lockedIcon: ImageCoverView;
  private type: SideButtonFeatures;
  private onUnlockedClick:
    | ((pt?: { x: number; y: number } | undefined) => Promise<void>)
    | undefined;
  private lockedReason: LockedReason | undefined;
  private image: ImageScaleView;
  private plate: ImageScaleView;
  private label: LangBitmapFontTextView;

  constructor(
    opts: SideButtonOpts & {
      type: SideButtonFeatures;
    },
  ) {
    super({
      width: 159,
      height: 168,
      centerOnOrigin: true,
      pressedOffsetY: 5,
      ...opts,
      icon: undefined,
    });

    const icon = opts.icon;
    const label = opts.label;

    this.type = opts.type;
    this.onUnlockedClick = opts.onClick;

    this.image = new ImageScaleView({
      superview: this,
      x: this.style.width * 0.5 + (icon.offsetX ?? 0),
      y: this.style.height * 0.5 + (icon.offsetY ?? 0),
      width: 104,
      height: 110,
      centerOnOrigin: true,
      ...icon,
    });

    this.plate = new ImageScaleView({
      superview: this,
      image: 'assets/ui/shared/frames/icon_plate.png',
      x: 90.5,
      y: 148,
      width: 159,
      height: 58,
      centerOnOrigin: true,
    });

    this.label = new LangBitmapFontTextView({
      superview: this.plate,
      x: this.plate.style.width * 0.5 - 2,
      y: this.plate.style.height * 0.5 - 5,
      width: 130,
      height: 47,
      align: 'center',
      verticalAlign: 'center',
      size: 22,
      color: '#b68351',
      wordWrap: false,
      font: bitmapFonts('Body'),
      localeText: () => label,
      centerOnOrigin: true,
    });

    this.lockedIcon = new ImageCoverView({
      superview: this,
      image: 'assets/ui/shared/icons/footer_locked.png',
      width: 50,
      height: 59,
      x: this.style.width - 55,
      y: 10,
      visible: false,
    });
  }

  public updateOpts(opts: Partial<SideButtonOpts>) {
    if (opts.label) {
      const { label } = opts;
      this.label.localeText = () => label;
      delete opts.label;
    }
    if (opts.icon) {
      this.image.updateOpts(opts.icon);
      delete opts.icon;
    }

    return super.updateOpts(opts);
  }

  public setLocked(locked: boolean, reason?: LockedReason) {
    if (locked) {
      const filter = new TintFilter(new Color(0, 0, 0, 0.2));
      this.image.setFilter(filter);
      this.plate.setFilter(filter);
    } else {
      this.image.removeFilter();
      this.plate.removeFilter();
    }
    this.lockedIcon.updateOpts({ visible: locked });
    this.onClickHandler = locked ? this.onLocked : this.onUnlockedClick;
    this.lockedReason = reason;
  }

  private async onLocked() {
    if (this.type === 'daycare') return;

    sounds.sfx.play('ok');

    await openPopupPromise('popupFeatureDisabled', {
      feature: this.type,
      reason: this.lockedReason,
    });
  }
}
